import React, { useEffect, useRef, useState } from 'react'
import { createPopperLite as createPopper, Instance, Options } from '@popperjs/core'
import useOnClickOutside from './useOnClickOutside'
import useConstCallback from './useConstCallback'

interface UsePopper {
  triggerRef: React.RefObject<HTMLElement>
  popupRef: React.RefObject<HTMLDivElement>
  innerRef: React.RefObject<HTMLDivElement>
  isOpen: boolean
  showHandler: () => void
  hideHandler: () => void
  create: () => void
  destroy: () => void
}

// We can't predict what props we'll pass here to trigger an update
type UpdateProps = unknown[]

export default function usePopper(
  popperConfig?: Options,
  updateProps: UpdateProps = [],
  targetRef?: React.RefObject<HTMLElement>,
  isOpenForce?: boolean,
): UsePopper {
  const [isOpen, setIsOpen] = useState(false)
  const popperInstance = useRef<Instance | null>()

  const triggerRef = useRef<HTMLElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)

  function create() {
    const triggerEl = targetRef?.current ?? triggerRef.current

    if (triggerEl && popupRef.current) {
      const popper = createPopper(triggerEl, popupRef.current, popperConfig || {})

      popper.forceUpdate()
      popperInstance.current = popper
    } else {
      throw new Error('Tooltip: triggerRef.current or popupRef.current are not defined (wrap trigger`s component in React.ForwardRef)')
    }
  }

  function destroy() {
    if (popperInstance.current) {
      popperInstance.current.destroy()
      popperInstance.current = null
    }
  }

  const showHandler = useConstCallback(() => {
    setIsOpen(true)
  }, [])

  const hideHandler = useConstCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => () => destroy(), [])

  useEffect(() => {
    if (isOpen) {
      popperInstance.current?.forceUpdate()
    }
    // Have to disable eslint here, as we have to pass all props that should trigger the update to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, popperInstance, isOpenForce, ...updateProps])

  useOnClickOutside([innerRef, triggerRef], hideHandler, isOpen && !isOpenForce)

  return {
    triggerRef,
    popupRef,
    innerRef,
    isOpen,
    showHandler,
    hideHandler,
    create,
    destroy,
  }
}
